import React, { ChangeEvent, createContext, Dispatch, SetStateAction, useMemo, useState } from 'react';
import { ApplicationUserModel, AssignedRelevosResponse, RelevoInterface } from 'api/types';

import { QuickContactResponse } from '../../modules/QuickContacts/model/quick-contact-response';
import { ItemAssignmentSelectMode } from '../../modules/RelevoAssignment/components/UserSelect/types';
import { ScannerMode } from '../../modules/RelevoAssignment/model/enums/scanner-mode.enum';
import { useRestaurantsData } from '../../services/restaurants';
import { useLocalStorage } from '../../utils/LocalStorage.hook';
import { RestaurantInfo } from '../SessionContext/SessionContext';
import { ASSIGN_RETURN_RESTAURANT_ID_LS_KEY, DELIVERY_NOTE_ENABLED_KEY } from './index';

type ScannedRelevos = RelevoInterface[];

export interface ScannedRelevosContextInterface {
  applicationUser: ApplicationUserModel | null;
  quickContact: QuickContactResponse | null;
  restaurantId: string | null;
  restaurant: RestaurantInfo | null;
  scannedRelevos: ScannedRelevos;
  assignedRelevos: AssignedRelevosResponse | null;
  setAssignedRelevos: Dispatch<SetStateAction<AssignedRelevosResponse | null>>;
  setScannedRelevos: Dispatch<SetStateAction<ScannedRelevos>>;
  setApplicationUser: (user: ApplicationUserModel | null) => void;
  setQuickContact: (quickContact: QuickContactResponse | null) => void;
  setRestaurantId: (restaurantId: string | null) => void;
  resetRestaurantId: () => void;
  mode: ScannerMode;
  selectItemMode: ItemAssignmentSelectMode;
  setSelectItemMode: (mode: ItemAssignmentSelectMode) => void;
  isDeliveryNoteEnabled: boolean;
  handleDeliveryNoteEnabled: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const ScannedRelevosContext = createContext<ScannedRelevosContextInterface | null>(null);

type ScannedRelevosContextProps = {
  mode: ScannerMode;
  children: React.ReactNode;
  locationId?: string;
};

export const ScannedRelevosContextProvider = ({ mode, children, locationId }: ScannedRelevosContextProps) => {
  const { restaurantIds, restaurants } = useRestaurantsData();
  const [lsRestaurantId, setLsRestaurantId] = useLocalStorage<string | null>(ASSIGN_RETURN_RESTAURANT_ID_LS_KEY, null);
  const defaultRestaurantId = useMemo(() => {
    if (locationId) {
      setLsRestaurantId(locationId);
      return locationId;
    }
    if (restaurantIds.length === 1) {
      setLsRestaurantId(restaurantIds[0]);
      return restaurantIds[0];
    }
    return null;
  }, [locationId, restaurantIds, setLsRestaurantId]);
  const [applicationUser, setApplicationUser] = useState<ApplicationUserModel | null>(null);
  const restaurant = useMemo(() => {
    return restaurants.find((r) => r.id === lsRestaurantId) || null;
  }, [lsRestaurantId, restaurants]);

  const [scannedRelevos, setScannedRelevos] = useState<ScannedRelevos>([]);
  const [assignedRelevos, setAssignedRelevos] = useState<AssignedRelevosResponse | null>(null);
  const [quickContact, setQuickContact] = useState<QuickContactResponse | null>(null);
  const [selectItemMode, setSelectItemMode] = useLocalStorage<ItemAssignmentSelectMode>(
    'selectItemMode',
    ItemAssignmentSelectMode.CAMERA_SCAN,
  );
  const [isDeliveryNoteEnabled, setIsDeliveryNoteEnabled] = useLocalStorage<boolean>(DELIVERY_NOTE_ENABLED_KEY, false);

  const resetLsRestaurantId = () => setLsRestaurantId(defaultRestaurantId);

  const handleDeliveryNoteEnabled = (e: ChangeEvent<HTMLInputElement>) => {
    setIsDeliveryNoteEnabled(e.target.checked);
  };

  return (
    <ScannedRelevosContext.Provider
      value={{
        applicationUser,
        quickContact,
        scannedRelevos,
        restaurantId: lsRestaurantId,
        restaurant: restaurant,
        setScannedRelevos,
        setApplicationUser,
        setQuickContact,
        setAssignedRelevos,
        setRestaurantId: setLsRestaurantId,
        resetRestaurantId: resetLsRestaurantId,
        assignedRelevos,
        mode,
        selectItemMode,
        setSelectItemMode,
        isDeliveryNoteEnabled,
        handleDeliveryNoteEnabled,
      }}
    >
      {children}
    </ScannedRelevosContext.Provider>
  );
};
